import {
  BUTTON_FACTORY_BUSY,
  INITIALIZE_WIZARD,
  WIZARD_CONTINUE,
  WIZARD_BACK,
  WIZARD_SET_CURRENT_PAGE,
  CREATE_BUTTON_SUCCESS,
  CREATE_BUTTON_ERROR,
  EDIT_BUTTON_SUCCESS,
  EDIT_BUTTON_ERROR,
  GET_BUTTON_DATA_SUCCESS,
  GET_BUTTON_DATA_ERROR,
  UPDATE_CODE_SNIPPET,
  SHOW_ACTIONABLE_ERROR_MODAL,
  HIDE_ACTIONABLE_ERROR_MODAL,
  SHOW_UNACTIONABLE_ERROR_MODAL,
  HIDE_UNACTIONABLE_ERROR_MODAL,
  SHOW_LEAVE_MODAL,
  HIDE_LEAVE_MODAL,
  SET_ERROR_LOADING_IMAGE,
  RESET_ERROR_LOADING_IMAGE,
  OPEN_MOBILE_PREVIEW,
  CLOSE_MOBILE_PREVIEW,
  UPDATE_FLOW_SPEC,
  BUTTON_FACTORY_DRAFT_BUSY,
  UPLOAD_BUTTON_IMAGE_ERROR,
  IS_DRAFT_BUTTON,
  RESET_BUTTON_FACTORY_STORE,
} from '../actions/actionsButtonFactory';
import {
  getNextStep,
  getOptionsForLanguageField,
  getPreviousStep,
} from '../utilsV3/buttonFactoryUtils';
import { getServerProps } from '../utilsV3/serverProps';
import _get from 'lodash/get';
import { transformServerFormatToClient } from '../lib/buttonModelV3';

const businessProfileLogoUrl = getSettingsLogoUrl();

const getDefaultValueForLanguage = () => {
  const serverProps = getServerProps();
  const countryAndLanguageDetails = _get(
    serverProps,
    'countryAndLanguageDetails'
  );
  const selectedCountry = _get(serverProps, 'selectedCountry');
  const languages = getOptionsForLanguageField(
    countryAndLanguageDetails,
    selectedCountry
  );
  // Get first language
  const language = _get(languages, '[0].value');
  return language || 'en';
};

export const LOGO_TARGET_BUTTON = 'BUTTON';
export const LOGO_TARGET_PROFILE = 'PROFILE';
export const LOGO_TARGET_NOLOGO = 'NOLOGO';

export const defaultButton = {
  accountIdentifierType: 'merchantAccountId',
  allowPrograms: 'No',
  amountType: 'variable',
  amountValue: '',
  business: getServerProps().accountIdentifier?.email,
  buttonImageUrl: '',
  buttonName: getServerProps().receiverName,
  buttonSize: 'large',
  countryCode: getServerProps().country,
  coverFeesEnabled: 'yes',
  extraVariables: {},
  extraVariablesView: '',
  frequencies: ['Monthly', 'Yearly'],
  itemNumber: '',
  language: getDefaultValueForLanguage(),
  listPrograms: 'no',
  logoTarget: businessProfileLogoUrl ? LOGO_TARGET_PROFILE : LOGO_TARGET_NOLOGO,
  logoUrl: '',
  merchantAmount1: '',
  merchantAmount2: '',
  merchantAmount3: '',
  noAdvancedVariables: 'yes',
  noCancelReturn: 'yes',
  noLogoImage: businessProfileLogoUrl ? 'no' : 'yes',
  noNote: 'no',
  noShipping: 'yes',
  receiverName: getServerProps().receiverName,
  programNames: [],
  purpose: '',
  receiverCurrency: getServerProps().primaryCurrency?.[0],
  recurringEnabled: 'yes',
  businessProfileLogoUrl,
  showAcceptedCardLogos: false,
  uploadNewLogo: 'no',
  useFullPageExperience: 'yes',
  usePaypalButtonStyle: 'yes',
  noBannerImage: 'yes',
  version: '3.0', // just to differntiate the experience where button was created
  status: 'D',
  draftVersion: '0',
  draftStep: '0',
};

const defaultState = {
  currentStep: 1,
  totalSteps: 4,
  busy: true,
  draftBusy: true,
  isMobilePreviewOpen: false,
  isDraft: false,
};

// get logo from business profile first and then payments settings
function getSettingsLogoUrl() {
  // business profile
  return _get(getServerProps(), 'businessImageUrls.url');
}

function getFormattedButton(payload) {
  // organizationName is not stored in BF service
  // fetching from server to make sure it gets pre-filled when user is editing/duplicating a button
  return Object.assign({}, transformServerFormatToClient(payload), {
    receiverName: getServerProps().receiverName,
    language: payload.language,
    offerProgram: payload.offerProgram,
    version: '3.0',
  });
}

export const initialState = {
  formattedButton: defaultButton,
};

export default function buttonFactoryV3(state = defaultState, action) {
  switch (action.type) {
    case IS_DRAFT_BUTTON: {
      return {
        ...state,
        isDraft: action.payload,
      };
    }
    case BUTTON_FACTORY_BUSY: {
      return {
        ...state,
        busy: true,
      };
    }

    case BUTTON_FACTORY_DRAFT_BUSY: {
      return {
        ...state,
        draftBusy: true,
      };
    }

    case UPLOAD_BUTTON_IMAGE_ERROR: {
      return {
        ...state,
        draftBusy: false,
      };
    }

    case INITIALIZE_WIZARD: {
      const button = action.payload;
      // We have to remove buttonName for personal acc. since it's one of the disallowed params
      // in constants/disallowedParamsToAccountType.js
      let clientSideState =
        action.userType !== 'PERSONAL'
          ? initialState
          : {
              formattedButton: {
                ...initialState.formattedButton,
                buttonName: '',
              },
            };
      if (button) {
        clientSideState = {
          formattedButton: getFormattedButton({
            ...button,
            businessImageUrls: action.businessImageUrls,
            merchantExperienceUrls: action.merchantExperienceUrls,
          }),
        };
      }

      return {
        ...state,
        button: action.payload,
        ...clientSideState,
        busy: false,
        draftBusy: false,
      };
    }

    case WIZARD_CONTINUE: {
      const {
        currentStep,
        formattedButton: { flowType },
      } = state;

      return {
        ...state,
        currentStep: getNextStep(currentStep, flowType),
        busy: false,
        draftBusy: false,
      };
    }

    case WIZARD_BACK: {
      const { currentStep } = state;

      return {
        ...state,
        currentStep: getPreviousStep(currentStep),
      };
    }

    case WIZARD_SET_CURRENT_PAGE: {
      return {
        ...state,
        currentStep: action.payload,
      };
    }

    case CREATE_BUTTON_SUCCESS: {
      return {
        ...state,
        button: action.payload,
        formattedButton: getFormattedButton({
          ...action.payload,
          businessImageUrls: action.businessImageUrls,
          merchantExperienceUrls: action.merchantExperienceUrls,
        }),
        busy: false,
        draftBusy: false,
        error: null,
        isButtonCreated: true,
      };
    }

    case CREATE_BUTTON_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
        draftBusy: false,
      };
    }

    case EDIT_BUTTON_SUCCESS: {
      const { currentStep, totalSteps, isDraft } = state;
      let isButtonCreated = isDraft && currentStep === totalSteps;

      return {
        ...state,
        button: action.payload,
        formattedButton: getFormattedButton({
          ...action.payload,
          businessImageUrls: action.businessImageUrls,
          merchantExperienceUrls: action.merchantExperienceUrls,
        }),
        busy: false,
        draftBusy: false,
        error: null,
        isButtonCreated: isButtonCreated,
        isButtonEdited: true,
      };
    }

    case EDIT_BUTTON_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
        draftBusy: false,
        isButtonCreated: false,
        isButtonEdited: true,
      };
    }

    case GET_BUTTON_DATA_SUCCESS: {
      const button = action.payload;
      const formattedButton = getFormattedButton({
        ...action.payload,
        businessImageUrls: action.businessImageUrls,
        merchantExperienceUrls: action.merchantExperienceUrls,
      });
      const draftStep = parseInt(formattedButton.draftStep || '0', 10);
      const currentStep =
        button.status === 'D' && draftStep > 0 ? draftStep : state.currentStep;
      const isDraft = button.status === 'D';

      return {
        ...state,
        button,
        formattedButton,
        currentStep,
        busy: false,
        draftBusy: false,
        isDraft: isDraft,
      };
    }

    case GET_BUTTON_DATA_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
        draftBusy: false,
      };
    }

    case UPDATE_CODE_SNIPPET: {
      return {
        ...state,
        customVariable: action.payload,
      };
    }

    case SHOW_ACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showActionableErrorModal: true,
      };
    }

    case HIDE_ACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showActionableErrorModal: false,
      };
    }

    case SHOW_UNACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showUnactionableErrorModal: true,
      };
    }

    case HIDE_UNACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showUnactionableErrorModal: false,
      };
    }

    case SHOW_LEAVE_MODAL: {
      return {
        ...state,
        showLeaveModal: true,
      };
    }

    case HIDE_LEAVE_MODAL: {
      return {
        ...state,
        showLeaveModal: false,
      };
    }

    case SET_ERROR_LOADING_IMAGE: {
      return {
        ...state,
        errorLoadingLogoImage: true,
      };
    }

    case RESET_ERROR_LOADING_IMAGE: {
      return {
        ...state,
        errorLoadingLogoImage: false,
      };
    }

    case OPEN_MOBILE_PREVIEW: {
      return {
        ...state,
        isMobilePreviewOpen: true,
      };
    }

    case CLOSE_MOBILE_PREVIEW: {
      return {
        ...state,
        isMobilePreviewOpen: false,
      };
    }

    case UPDATE_FLOW_SPEC: {
      return {
        ...state,
        totalSteps: action.payload.totalSteps,
        formattedButton: {
          ...state.formattedButton,
          flowType: action.payload.flowType,
        },
      };
    }

    // Reset button factory store (only for MEP/federated context)
    // Returns only defaultState without initialState to avoid showing pre-filled default values in preview
    case RESET_BUTTON_FACTORY_STORE: {
      return {
        ...defaultState,
      };
    }

    default:
      return state;
  }
}
